import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "./Sidebar";
import APIs from "../../APIs";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import GoogleAddressAutocomplete from "../../components/GoogleAddressAutocomplete";
import { GOOGLE_MAP_KEY } from "../../Utils/Utils";
import { useMyLocation } from "../../hooks/LocationContext";

const AddNewAddress = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editAddressData = location.state?.address || null;
  const { location: currentLocation, loaded } = useMyLocation();
  console.log("current my location3", currentLocation);
  const [formData, setFormData] = useState({
    address: "",
    landmark: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    type: "Home",
  });

  const [coordinates, setCoordinates] = useState({ lat: currentLocation.lat, lng: currentLocation.lng }); // Default Delhi
  const [map, setMap] = useState(null);
  const geocoderRef = useRef(null);

  const userData = JSON.parse(sessionStorage.getItem("userData"));
  const user_id = userData?.id || userData?.user_id;

  // ✅ Load Google Maps
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_KEY,
  });

  // ✅ Initialize Geocoder
  useEffect(() => {
    if (isLoaded && window.google && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  // ✅ Prefill if editing
  useEffect(() => {
    if (editAddressData) {
      setFormData({
        address: editAddressData.address || "",
        landmark: editAddressData.landmark || "",
        city: editAddressData.city || "",
        state: editAddressData.state || "",
        country: editAddressData.country || "",
        zipCode: editAddressData.zip_code || "",
        type: editAddressData.address_type || "Home",
      });
      setCoordinates({
        lat: parseFloat(editAddressData.address_lat),
        lng: parseFloat(editAddressData.address_long),
      });
    }
  }, [editAddressData]);

  // ✅ Reverse Geocode (map → address fields)
  const fetchAddress = useCallback((lat, lng) => {
    if (!geocoderRef.current) return;
    const location = { lat: parseFloat(lat), lng: parseFloat(lng) };

    geocoderRef.current.geocode({ location }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];
        const components = place.address_components;
        let city = "",
          state = "",
          country = "",
          postalCode = "",
          landmark = "";

        components.forEach((component) => {
          const types = component.types;
          if (types.includes("locality")) city = component.long_name;
          if (types.includes("administrative_area_level_1"))
            state = component.long_name;
          if (types.includes("country")) country = component.long_name;
          if (types.includes("postal_code")) postalCode = component.long_name;
          if (types.includes("sublocality") || types.includes("neighborhood"))
            landmark = component.long_name;
        });

        const shortAddress = place.formatted_address
          .split(",")
          .slice(0, 2)
          .join(",");

        setFormData((prev) => ({
          ...prev,
          address: shortAddress || prev.address,
          city: city || prev.city,
          state: state || prev.state,
          country: country || prev.country,
          zipCode: postalCode || prev.zipCode,
          landmark: landmark || prev.landmark,
        }));
      } else {
        toast.error("Unable to get address for this location");
      }
    });
  }, []);

  // ✅ Map drag stop → fetch new address
  const handleCenterChanged = () => {
    if (!map) return;
    const center = map.getCenter();
    const lat = center.lat();
    const lng = center.lng();
    setCoordinates({ lat, lng });
    fetchAddress(lat, lng);
  };

  // ✅ On map load
  const handleMapLoad = (mapInstance) => {
    setMap(mapInstance);
    if (editAddressData) {
      mapInstance.setCenter({
        lat: parseFloat(editAddressData.address_lat),
        lng: parseFloat(editAddressData.address_long),
      });
    }
  };

  // ✅ Helper: Forward geocoding (manual address → lat/lng)
  const getCoordinatesFromAddress = async (address) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=${GOOGLE_MAP_KEY}`
      );
      const data = await response.json();
      if (data.status === "OK" && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        return { lat, lng };
      } else {
        toast.error("Could not find location for entered address");
        return { lat: 0, lng: 0 };
      }
    } catch (error) {
      console.error("Error getting coordinates:", error);
      toast.error("Error fetching coordinates from address");
      return { lat: 0, lng: 0 };
    }
  };

  // ✅ Submit address (handles both manual input + map)
  const handleSubmit = async () => {
    if (!user_id) {
      toast.error("User not logged in!");
      return;
    }

    try {
      let lat = coordinates.lat;
      let lng = coordinates.lng;

      // 🧭 If address entered manually (no map move)
      if (!lat || !lng || (lat === 28.6139 && lng === 77.209)) {
        const fullAddress = `${formData.address}, ${formData.city}, ${formData.state}, ${formData.country}, ${formData.zipCode}`;
        const geo = await getCoordinatesFromAddress(fullAddress);
        lat = geo.lat;
        lng = geo.lng;
        if (!lat || !lng) {
          toast.error("Please enter a valid address location");
          return;
        }
      }

      const payload = {
        user_id,
        address_lat: lat,
        address_long: lng,
        address: formData.address,
        landmark: formData.landmark,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        zip_code: formData.zipCode,
        address_type: formData.type,
      };

      let response;
      if (editAddressData?.address_id) {
        payload.address_id = editAddressData.address_id;
        response = await APIs.post("address/editAddress", payload);
      } else {
        response = await APIs.post("address/addAddress", payload);
      }

      if (response.data.success || response.data.status) {
        toast.success(
          editAddressData
            ? "Address updated successfully!"
            : "Address added successfully!"
        );
        setTimeout(() => navigate("/myaddress"), 1000);
      } else {
        toast.error(response.data.message || "Failed to save address");
      }
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Something went wrong while saving address");
    }
  };

  const handleAddressSelect = (place) => {
    console.log("Selected address:", place);
    console.log("Lat:", place.lat);
    console.log("Lng:", place.lng);
    setCoordinates({ lat: place.lat, lng: place.lng });
    fetchAddress(place.lat,place.lng)
  };

  if (loadError) return <p>Error loading Google Maps!</p>;
  if (!isLoaded) return <p>Loading map...</p>;

  return (
    <div className="page-content">
      <section className="step-section-md">
        <div className="container">
          <div className="row">
            <div className="col-lg-3">
              <Sidebar />
            </div>
            <div className="col-lg-9">
              <div className="card mt-30">
                <div className="card-header bg-white">
                  <h5 className="card-title">
                    {editAddressData ? "Edit Address" : "Add Address"}
                  </h5>
                </div>
                <div className="card-body">
                  {/* ✅ Google Map */}
                  <div
                    style={{
                      width: "100%",
                      height: "300px",
                      borderRadius: "8px",
                      border: "2px solid #54bf66",
                      marginBottom: "20px",
                      position: "relative",
                    }}
                  >
                    <GoogleMap
                      center={coordinates}
                      zoom={14}
                      key={`${coordinates.lat}-${coordinates.lng}`}
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      onLoad={handleMapLoad}
                      onDragEnd={handleCenterChanged}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -100%)",
                        fontSize: "30px",
                        zIndex: "10",
                      }}
                    >
                      📍
                    </div>
                  </div>
                  <div style={{ position: "static" }}>
  <GoogleAddressAutocomplete onPlaceSelected={handleAddressSelect} />
</div>
                  <div className="row">
                    {[
                      { label: "Apartment / Home", name: "address" },
                      { label: "Landmark", name: "landmark" },
                      { label: "City", name: "city" },
                      { label: "State", name: "state" },
                      { label: "Country", name: "country" },
                      { label: "Zip Code", name: "zipCode" },
                    ].map((field) => (
                      <div className="col-md-6 mb-3" key={field.name}>
                        <label htmlFor={field.name} className="form-label">
                          {field.label}
                        </label>
                        <input
                          className="form-control"
                          id={field.name}
                          type="text"
                          name={field.name}
                          value={formData[field.name]}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              [e.target.name]: e.target.value,
                            }))
                          }
                          readOnly
                        />
                      </div>
                    ))}

                    <div className="col-md-6 mb-3">
                      <label htmlFor="type" className="form-label">
                        Address Type
                      </label>
                      <select
                        className="form-control form-select"
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            type: e.target.value,
                          }))
                        }
                      >
                        <option value="Home">Home</option>
                        <option value="Office">Office</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="card-footer bg-white">
                  <button className="btn btn-success" onClick={handleSubmit}>
                    {editAddressData ? "Update Address" : "Add Address"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddNewAddress;
